import { useMutation, useQuery } from "@apollo/client";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { useParams } from "@/router";
import NonMemberEmploymentFormModal, { type NonMemberEmploymentFormData } from "./NonMemberEmploymentFormModal";
import { CREATE_NON_MEMBER_EMPLOYMENT_MUTATION } from "../../person/mutations";
import { GET_EMPLOYMENT_DETAILS_QUERY, GET_JOB_TITLES_QUERY } from "../../person/queries";

interface CreateNonMemberEmploymentModalProps {
  onClose(): void;
  personId?: string;
}

export default function CreateNonMemberEmploymentModal({ onClose, personId }: CreateNonMemberEmploymentModalProps) {
  const { pgaId } = useParams("/people/:pgaId/career/employment");

  const { data: jobTitlesData } = useQuery(GET_JOB_TITLES_QUERY, {
    variables: { personId: personId! },
    skip: !personId
  });

  const nonMemberEmploymentJobTitles = jobTitlesData?.person.availableJobTitles || [];

  const [createNonMemberEmployment] = useMutation(CREATE_NON_MEMBER_EMPLOYMENT_MUTATION, {
    refetchQueries: [
      { query: GET_EMPLOYMENT_DETAILS_QUERY, variables: { pgaId } },
      { query: PERSON_DETAILS_HEADER_QUERY, variables: { pgaId } }
    ],
    awaitRefetchQueries: true
  });

  const onSubmit = async ({ facilityId, jobTitle, startDate, endDate, note }: NonMemberEmploymentFormData) => {
    const trimmedNote = note?.trim();

    const { data } = await createNonMemberEmployment({
      variables: {
        input: {
          personId: personId!,
          facilityId,
          jobTitle,
          startedOn: startDate || null,
          endedOn: endDate || null,
          note: trimmedNote ? { content: trimmedNote, priority: "NONE", title: "Add Employment" } : undefined
        }
      }
    });

    return data?.createNonMemberEmployment || { success: false, message: "Failed to create employment" };
  };

  return (
    <NonMemberEmploymentFormModal
      open
      headline="Add Employment"
      jobTitles={nonMemberEmploymentJobTitles}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  );
}
